import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { vt, openCta } from "@/lib/industry";
import { Label } from "@/components/ui/label";
import { api, errMsg, sessionId } from "@/lib/api";

const TIMES = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
const ymd = (d) => format(d, "yyyy-MM-dd");
const ampm = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`; };

export const BrandButton = ({ className = "", variant = "solid", ...p }) => (
  <button
    {...p}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 h-11 text-sm font-semibold transition-[transform,background-color,opacity] duration-200 active:scale-[.97] disabled:opacity-50 ${
      variant === "solid" ? "bg-bp text-white hover:opacity-90" : variant === "accent" ? "bg-ba text-white hover:opacity-90" : "border border-slate-300 bg-white/80 text-slate-900 hover:bg-white"
    } ${className}`}
  />
);

function SlotPicker({ builderId, cfg, date, setDate, slot, setSlot }) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!date) { setSlots(null); return; }
    setSlots(null);
    api.get(`/public/booking/${builderId}`, { params: { date: ymd(date) } }).then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [builderId, date]);
  const today = new Date(new Date().toDateString());
  const last = new Date(today.getTime() + cfg.days_ahead * 864e5);
  const closed = (d) => d < today || d > last || !cfg.days.includes((d.getDay() + 6) % 7);
  return (
    <div className="space-y-2" data-testid="booking-slot-picker">
      <Label>Pick a day</Label>
      <div className="rounded-xl border flex justify-center"><Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setSlot(""); }} disabled={closed} data-testid="booking-calendar" /></div>
      {date && (
        <div>
          <Label>Available times · {format(date, "EEE d MMM")}</Label>
          {slots === null ? <p className="text-sm text-slate-400 py-2">Checking availability…</p>
            : slots.length === 0 ? <p className="text-sm text-slate-500 py-2" data-testid="booking-no-slots">No times left on this day. Please pick another day.</p>
            : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                {slots.map((t) => (
                  <button type="button" key={t} onClick={() => setSlot(t)} className={`h-10 rounded-full border text-sm font-medium transition-colors ${slot === t ? "bg-bp text-white border-transparent" : "bg-white hover:border-slate-500"}`} data-testid={`booking-slot-${t}`}>{ampm(t)}</button>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

function RequestTime({ date, setDate, time, setTime }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="w-full h-10 rounded-md border px-3 text-left text-sm flex items-center gap-2" data-testid="lead-date-trigger">
              <CalendarDays className="w-4 h-4 text-slate-500" />{date ? format(date, "d MMM yyyy") : "Pick a date"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().toDateString())} data-testid="lead-date-calendar" />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <Label>Time</Label>
        <Select value={time} onValueChange={setTime}>
          <SelectTrigger data-testid="lead-time-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function LeadDialog({ open, onOpenChange, kind, builderId, context = {}, title, onSent }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("11:00");
  const [slot, setSlot] = useState("");
  const [cfg, setCfg] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const viewing = kind !== "interest";
  const live = viewing && cfg?.enabled;
  const heading = title || (kind === "design" ? "Book a design studio appointment" : viewing ? vt().book : "Register your interest");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (!open || !viewing || !builderId) return;
    api.get(`/public/booking/${builderId}`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, [open, viewing, builderId]);

  const close = (o) => { onOpenChange(o); if (!o) { setDone(null); setSlot(""); setDate(null); } };

  const submit = async (e) => {
    e.preventDefault();
    if (viewing && !date) return toast.error("Please choose a date");
    if (live && !slot) return toast.error("Please choose a time");
    setBusy(true);
    try {
      const { data } = await api.post("/public/leads", {
        kind, builder_id: builderId, ...f, message: context.message_prefix ? `[${context.message_prefix}] ${f.message}`.trim() : f.message, session_id: sessionId(),
        preferred_at: viewing && !live ? `${format(date, "EEE d MMM yyyy")} ${time}` : "",
        slot_date: live ? ymd(date) : "", slot_time: live ? slot : "",
        development_id: context.development_id || "", plot_id: context.plot_id || "", house_type_id: context.house_type_id || "",
      });
      setF({ name: "", email: "", phone: "", message: "" });
      if (data.booked) { setDone(data.when); return; }
      onSent?.();
      toast.success(viewing ? "Appointment requested — we'll confirm shortly." : "Thanks! We'll be in touch soon.");
      close(false);
    } catch (err) {
      toast.error(errMsg(err));
      if (err.response?.status === 409) { const d = date; setDate(null); setTimeout(() => setDate(d), 0); setSlot(""); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md rounded-2xl max-h-[92dvh] overflow-y-auto" data-testid="lead-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="booking-confirmed">
            <CalendarCheck className="w-12 h-12 mx-auto text-bp" />
            <DialogTitle className="font-serif text-2xl mt-3">You're booked in</DialogTitle>
            <p className="text-lg font-semibold mt-3" data-testid="booking-confirmed-when">{done}</p>
            <DialogDescription className="mt-2">We've emailed you a confirmation. See you then!</DialogDescription>
            <BrandButton className="w-full mt-5" onClick={() => close(false)} data-testid="booking-confirmed-done">Done</BrandButton>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{heading}</DialogTitle>
              <DialogDescription>{context.label || (live ? "Choose a time that suits you. It's confirmed straight away." : "Our sales team will get back to you personally.")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              {live ? <SlotPicker builderId={builderId} cfg={cfg} date={date} setDate={setDate} slot={slot} setSlot={setSlot} />
                : viewing && cfg && <RequestTime date={date} setDate={setDate} time={time} setTime={setTime} />}
              <div><Label>Full name</Label><Input required value={f.name} onChange={set("name")} data-testid="lead-name-input" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Email</Label><Input required type="email" value={f.email} onChange={set("email")} data-testid="lead-email-input" /></div>
                <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="lead-phone-input" /></div>
              </div>
              <div><Label>Message (optional)</Label><Textarea rows={2} value={f.message} onChange={set("message")} data-testid="lead-message-input" /></div>
              <BrandButton type="submit" disabled={busy} className="w-full" data-testid="lead-submit-button">
                {busy ? "Sending…" : live ? (slot ? `Book ${format(date, "EEE d MMM")} at ${ampm(slot)}` : "Choose a time") : kind === "design" ? "Request appointment" : viewing ? "Request viewing" : "Register interest"}
              </BrandButton>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LeadCTA({ builderId, context, className = "" }) {
  const [kind, setKind] = useState(null);
  const [main, ...rest] = vt().cta || [];
  const go = (l) => openCta(l, { builder_id: builderId, house_type_id: context?.house_type_id || "" });
  return (
    <>
      <div className={`flex gap-2 ${className}`}>
        <BrandButton onClick={() => (main ? go(main) : setKind("viewing"))} data-testid="cta-book-viewing">{vt().book}</BrandButton>
        {rest.map((l, i) => <BrandButton key={l.url} variant="ghost" className="hidden lg:inline-flex" onClick={() => go(l)} data-testid={`cta-link-${i}`}>{l.label}</BrandButton>)}
        <BrandButton variant="ghost" onClick={() => setKind("interest")} data-testid="cta-register-interest">Register interest</BrandButton>
      </div>
      <LeadDialog open={!!kind} onOpenChange={(o) => !o && setKind(null)} kind={kind || "interest"} builderId={builderId} context={context} />
    </>
  );
}
